import React, { useState, useEffect, useRef } from 'react';
import {
    Formik,
    Form,
    Field,
    CccisdInput,
    CccisdFieldWrapper,
    CccisdSelect,
    CccisdDatepicker,
    CccisdToggle,
} from 'cccisd-formik';
import Table from 'cccisd-graphql-table';
import PropTypes from 'prop-types';
import Tooltip from 'cccisd-tooltip';
import IconInfo from 'cccisd-icons/info2';
import { client } from 'cccisd-apollo';
import { UserCheck } from 'cccisd-laravel-nexus';

import sitesQuery from './sites.graphql';
import supervisorsQuery from './supervisorsBySite.graphql';
import RequiredFieldLabel from './RequiredFieldLabel';
import style from './style.css';

const siteColumns = [
    {
        name: 'group.groupId',
        label: 'Site ID',
        sort: true,
        filter: true,
    },
    {
        name: 'group.label',
        label: 'Site',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.agency.group.label',
        label: 'Agency',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.organization.group.label',
        label: 'Organization',
        sort: true,
        filter: true,
    },
    {
        name: 'ancestorGroups.collaborator.group.label',
        label: 'Collaborator',
        sort: true,
        filter: true,
    },
];

const Fortress = window.cccisd && window.cccisd.fortress;

const exitReasonOptions = window.cccisd.appDefs.pawn.fields
    .filter(field => field.handle === 'exitReasonCareCoordinator')[0]
    .values.map(option => ({ value: option.value, label: option.name }));

const isEmail = email => /\S+@\S+\.\S+/.test(email);

const SupervisorSelect = ({ siteId, value, onChange }) => {
    const [supervisors, setSupervisors] = useState([]);

    useEffect(() => {
        let isCurrent = true;

        const getSupervisors = async () => {
            if (!siteId) {
                setSupervisors([]);
                return;
            }
            const response = await client.query({
                query: supervisorsQuery,
                variables: { siteIds: [siteId] },
                fetchPolicy: 'network-only',
                errorPolicy: 'all',
            });
            if (isCurrent) {
                setSupervisors(response.data.roles.supervisorList);
            }
        };
        getSupervisors();

        return () => {
            isCurrent = false;
        };
    }, [siteId]);

    return (
        <Field name="supervisor">
            {({ field, form }) => (
                <CccisdFieldWrapper
                    field={field}
                    form={form}
                    label={<RequiredFieldLabel isRequired>Supervisor:</RequiredFieldLabel>}
                >
                    <select
                        className="form-select"
                        value={value || ''}
                        onChange={e => onChange(e.target.value)}
                        style={{ width: 'auto' }}
                    >
                        <option value="">-- Select Supervisor --</option>
                        {supervisors.map(sup => (
                            <option key={sup.pawn.pawnId} value={sup.pawn.pawnId}>
                                {sup.user.firstName} {sup.user.lastName} &lt;{sup.user.username}&gt;
                            </option>
                        ))}
                    </select>
                </CccisdFieldWrapper>
            )}
        </Field>
    );
};

SupervisorSelect.propTypes = {
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    onChange: PropTypes.func,
};

const CareCoordinatorForm = props => {
    const { onSubmit, initialValues, isEdit } = props;
    const [notice, setNotice] = useState({ text: '', level: '' });
    const [isCheckingUser, setIsCheckingUser] = useState(false);
    const latestCheck = useRef(0);
    const currentRole = Fortress.user && Fortress.user.acting.role.handle;
    const isSupervisor = currentRole === 'supervisor';
    // Auto-fills/hides the site field only - Site Admin still picks a Supervisor manually.
    const isSiteAdmin = currentRole === 'siteAdmin';
    const hasFixedSite = isSupervisor || isSiteAdmin;

    const onHandleSubmit = async values => {
        await onSubmit({ ...values, site: values.site[0], role: 'careCoordinator' });
    };

    const validate = values => {
        const errors = {};

        if (!values.firstName) {
            errors.firstName = 'First Name is required.';
        }
        if (!values.lastName) {
            errors.lastName = 'Last Name is required.';
        }
        if (!values.email) {
            errors.email = 'Email is required.';
        } else if (!isEmail(values.email)) {
            errors.email = 'Please enter a valid email.';
        }
        if (values.phoneNumber && !/^\d{10}$/.test(values.phoneNumber)) {
            errors.phoneNumber = 'Phone number must be 10 digits, with no dashes or parenthesis.';
        }
        if (!hasFixedSite) {
            if (values.site.length < 1) {
                errors.site = 'Site is required.';
            }
            if (values.site.length > 1) {
                errors.site = 'You may select only 1 Site.';
            }
            if (values.enrolledInSmartwrap && !values.supervisor) {
                errors.supervisor = 'Supervisor is required.';
            }
        }

        return errors;
    };

    /**
     * When an email is entered into the email field,
     * check if the username exists already and is available
     * to use with this username/group combination.
     *
     * Also check when the group is selected or changed.
     */
    const userCheck = async (values, newGroup, setFieldValue, siteLabel = values.siteName) => {
        const checkUserDelay = 1000;
        const email = values.email;
        const group = newGroup || (Array.isArray(values.site) ? values.site[0] : null);
        const checkId = ++latestCheck.current;

        if (!email) {
            setIsCheckingUser(false);
            return;
        }

        // needed on submit
        setFieldValue('existingUser', false);
        setFieldValue('available', true);

        // editing within the pawn's own site
        if (isEdit && String(group) === String(initialValues.site[0])) {
            setIsCheckingUser(false);
            setNotice({ text: '', level: 'success' });
            return;
        }

        const checkUserData = {
            username: email,
            group,
            role: 'careCoordinator',
        };

        setIsCheckingUser(true);
        const userCheckResponse = await UserCheck(checkUserData, checkUserDelay);

        // a newer check has started
        if (checkId !== latestCheck.current) {
            return;
        }
        setIsCheckingUser(false);

        if (userCheckResponse.available) {
            if (isEdit) {
                setNotice({ text: '', level: 'success' });
                return;
            }
            // show username is available
            setNotice({ text: 'Username is available!', level: 'success' });
            // check if existing user
            // if yes, pre populate first and last name
            if (userCheckResponse.user) {
                setFieldValue('existingUser', true);

                setNotice({
                    text: 'User already exists in another group, filling details',
                    level: 'info',
                });
                setFieldValue('firstName', userCheckResponse.user.first_name || '');
                setFieldValue('lastName', userCheckResponse.user.last_name || '');
            }
        } else {
            setFieldValue('existingUser', true);
            setFieldValue('available', false);
            // user is not available
            // maybe same group?
            // show error on form
            setNotice({
                text: `User already exists in ${siteLabel || 'this group'}`,
                level: 'danger',
            });
        }
    };

    return (
        <Formik onSubmit={e => onHandleSubmit(e)} validate={e => validate(e)} initialValues={initialValues}>
            {({ handleSubmit, setFieldValue, isSubmitting, values }) => (
                <Form>
                    {notice.text && <div className={`alert alert-${notice.level}`}>{notice.text}</div>}
                    <Field name="email">
                        {({ field, form }) => (
                            <CccisdInput
                                field={{
                                    ...field,
                                    onBlur: event => {
                                        field.onBlur(event);
                                        userCheck(form.values, null, form.setFieldValue);
                                    },
                                }}
                                form={form}
                                label={<RequiredFieldLabel isRequired>Email:</RequiredFieldLabel>}
                            />
                        )}
                    </Field>
                    {/* an existing user's names are filled in and kept as they are */}
                    <Field
                        name="firstName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>First Name:</RequiredFieldLabel>}
                        disabled={!isEdit && Boolean(values.existingUser)}
                    />
                    <Field
                        name="lastName"
                        component={CccisdInput}
                        label={<RequiredFieldLabel isRequired>Last Name:</RequiredFieldLabel>}
                        disabled={!isEdit && Boolean(values.existingUser)}
                    />
                    <Field name="adminId" component={CccisdInput} label="ID Number:" />
                    <Field
                        name="phoneNumber"
                        component={CccisdInput}
                        label="Phone Number (no dashes or parenthesis - 10 digits only):"
                    />
                    {!hasFixedSite && (
                        <Field name="site" enableReinitialize>
                            {({ field, form }) => (
                                <CccisdFieldWrapper
                                    field={field}
                                    form={form}
                                    label={<RequiredFieldLabel isRequired>Site:</RequiredFieldLabel>}
                                >
                                    <Table
                                        query={sitesQuery}
                                        rowKey="group.groupId"
                                        columns={siteColumns}
                                        csvFilename="Sites.csv"
                                        showRowActions={false}
                                        perPage={10}
                                        onRowClick={row => {
                                            const selectedSiteId = row['group.groupId'];
                                            setFieldValue('site', [selectedSiteId]);
                                            setFieldValue('siteName', row['group.label']);
                                            userCheck(values, selectedSiteId, setFieldValue, row['group.label']);
                                        }}
                                        setRowClass={row =>
                                            row['group.groupId'] === field.value[0] && style.selectedRow
                                        }
                                    />
                                </CccisdFieldWrapper>
                            )}
                        </Field>
                    )}

                    <div>
                        <Field
                            name="enrolledInSmartwrap"
                            component={CccisdToggle}
                            label={
                                <span>
                                    Enrolled in SmartWrap{' '}
                                    <Tooltip title="If enabled, this Care Coordinator will have log-in access to the SmartWrap dashboard">
                                        <span className="text-primary">
                                            <IconInfo />
                                        </span>
                                    </Tooltip>
                                </span>
                            }
                        />
                        {values.enrolledInSmartwrap && (
                            <div
                                style={{
                                    paddingLeft: '20px',
                                }}
                            >
                                <p>
                                    <strong>SmartWrap Page Permissions for Care Coordinator:</strong>
                                </p>
                                <Field
                                    name="administerPageAccess"
                                    component={CccisdToggle}
                                    label="Administer Page Access"
                                />
                                <Field
                                    name="dashboardPageAccess"
                                    component={CccisdToggle}
                                    label="Dashboard Page Access"
                                />
                                <Field name="managePageAccess" component={CccisdToggle} label="Manage Page Access" />
                                <>
                                    <div>
                                        <p>
                                            <strong>SMART-Alerts: Communication Preference Settings</strong>
                                        </p>
                                        <Field
                                            name="receiveAlertsImmediately"
                                            component={CccisdToggle}
                                            label="Care Coordinator receives SMART-Alerts immediately?"
                                        />
                                        <Field
                                            name="receiveAlertsDigest"
                                            component={CccisdToggle}
                                            label="Care Coordinator receives SMART-Alerts in Daily Digest?"
                                        />

                                        <p>
                                            <strong>SMART-Notices: Communication Preference Settings</strong>
                                        </p>
                                        <Field
                                            name="receiveNotifications"
                                            component={CccisdToggle}
                                            label="Care Coordinator receives weekly digest SMART-Notices?"
                                        />
                                    </div>
                                </>

                                {!isSupervisor && (
                                    <SupervisorSelect
                                        siteId={values.site[0]}
                                        value={values.supervisor}
                                        onChange={val => setFieldValue('supervisor', val)}
                                    />
                                )}

                                {values.supervisor && (
                                    <div>
                                        <hr />
                                        {!isSupervisor && (
                                            <p>
                                                <strong>
                                                    Select notifications to send to the selected Supervisor:
                                                </strong>
                                            </p>
                                        )}
                                        <p>
                                            <strong>SMART-Alerts: Communication Preference Settings</strong>
                                        </p>
                                        <Field
                                            name="supervisorReceiveAlertsImmediate"
                                            component={CccisdToggle}
                                            label="Supervisor also receives the Care Coordinator's immediate SMART-Alerts?"
                                        />
                                        <Field
                                            name="supervisorReceiveAlertsDigest"
                                            component={CccisdToggle}
                                            label="Supervisor also receives the Care Coordinator's SMART-Alerts in Daily Digest?"
                                        />

                                        <p>
                                            <strong>SMART-Notices: Communication Preference Settings</strong>
                                        </p>
                                        <Field
                                            name="supervisorReceiveNotifications"
                                            component={CccisdToggle}
                                            label="Supervisor also receives the Care Coordinator's SMART-Notices in Weekly Digest?"
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {isEdit && (
                        <>
                            <p>
                                <strong>
                                    Notice: This care coordinator will be archived if exit date and exit reason is set.
                                </strong>
                            </p>
                            <p>
                                If archived, please navigate to the Youth Roster tab to assign a new care coordinator
                                for all actively enrolled youth who previously received Wraparound care from this care
                                coordinator. If you do not, the system will show that no care coordinator is assigned.
                            </p>
                            <Field
                                name="exitDate"
                                component={CccisdDatepicker}
                                label="Exit Date:"
                                reactDatepickerProps={{ disabled: !isEdit }}
                            />
                            <Field
                                name="exitReasonCareCoordinator"
                                component={CccisdSelect}
                                label="Exit Reason:"
                                disabled={!isEdit}
                                options={[
                                    { value: '', label: '--Please select an exit reason--' },
                                    ...exitReasonOptions,
                                ]}
                            />
                        </>
                    )}

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            marginTop: '1em',
                        }}
                    >
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => handleSubmit()}
                            disabled={isSubmitting || isCheckingUser || values.available === false}
                        >
                            Submit
                        </button>
                    </div>
                </Form>
            )}
        </Formik>
    );
};

CareCoordinatorForm.propTypes = {
    onSubmit: PropTypes.func,
    initialValues: PropTypes.object,
    isEdit: PropTypes.bool,
};

export default CareCoordinatorForm;
